import { Box, BoxProps, Card, Link, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { FC, MouseEventHandler } from "react";
import { useDeploymentDetailContext } from "~/pages/deployment/useDeploymentDetailContext";
import { SxProps } from "@mui/system";
import { merge } from "lodash";
import { DeploymentStatus, InstanceStatus } from "~/gen/api/deploy";
import LogViewer, { useLogViewerController, LogViewerProvider } from "../backups/LogViewer";
import numeral from "numeral";
import ReadonlyField from "../input/ReadonlyField";
import { useRevealPass } from "~/utils/useRevealPass";


const DeploymentInfo: FC = () => {
  const { currDatabase } = useDeploymentDetailContext()

  const logCtrl = useLogViewerController()
  const { showLogInViewer } = logCtrl

  const { showPass, endAdornment } = useRevealPass()

  return (
    <LogViewerProvider value={logCtrl}>
      <Card>
        <Table>
          <TableBody>
            <TRow>
              <InfoPiece label="Engine">
                {`${currDatabase?.engineVersion || ''} ${currDatabase?.engineEdition || ''}`}
              </InfoPiece>
              <InfoPiece label="Database Status">
                <StatusIndicator 
                  color={(
                    !currDatabase ? 'inherit'
                    : currDatabase.status === InstanceStatus.Online ? 'success.main'
                    : ([DeploymentStatus.InProgress, DeploymentStatus.Deleting, DeploymentStatus.Restarting, DeploymentStatus.Stopping]
                        .some(x => x === currDatabase.deploymentStatus)) ? 'text.disabled'
                    : currDatabase.deploymentStatus === DeploymentStatus.Warning ? 'warning.main'
                    : currDatabase.deploymentStatus === DeploymentStatus.Failed ? 'error.main'
                    : 'text.disabled') as BoxProps['color']
                  }
                  showViewLog={!!currDatabase && currDatabase.deploymentStatus === DeploymentStatus.Failed && !!currDatabase.message}
                  viewLogClick={(e:any) => showLogInViewer(e, currDatabase?.message || '')}
                  title={
                    !currDatabase ? undefined
                    : currDatabase.status === InstanceStatus.Online ? 'Active'
                    : (currDatabase.deploymentStatus !== undefined && currDatabase.deploymentStatus !== DeploymentStatus.Success
                        ? (DeploymentStatus[currDatabase.deploymentStatus] || '').replace(/[A-Z]+/g, (m:string) => { return ' ' + m}) as any
                        : 'Offline')
                  }
                />
              </InfoPiece>
            </TRow>

            <TRow>
              <InfoPiece label="Database Size">
                {!(currDatabase?.size) ? 'N/A' :
                  currDatabase?.sizeText &&
                    currDatabase?.sizeText.toUpperCase()
                      .replace(/\d+/, (m) => numeral(m).format('0,0'))
                }
              </InfoPiece>
              <InfoPiece label="Hosting Option">
                {currDatabase?.hostingSpecification}
              </InfoPiece>
            </TRow>
            {/* <TRow>
              <InfoPiece label="Network Connectivity">
                {'???'}
              </InfoPiece>
            </TRow> */}
            <TRow>
              <InfoPiece label="Connection String">
                <ReadonlyField 
                  fullWidth
                  variant="outlined"
                  type={showPass ? 'text' : 'password'} 
                  InputProps={{ endAdornment }}
                  size="small"
                  value={currDatabase?.connectionString || ''}
                />
              </InfoPiece>
            </TRow>
          </TableBody>
        </Table>
      </Card>
      <LogViewer />
    </LogViewerProvider>
  )
}

export default DeploymentInfo;

export const TRow: FC = ({ children }) => (
  <TableRow>
    <TableCell>
      <Box 
        sx={{ 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'row',
          '& .cell': { flex: 1 },
        }}
      >
        {children}
      </Box>
    </TableCell>
  </TableRow>
)

export const InfoPiece: FC<{label:string}> = ({ label, children }) => (
  <Stack direction="row" className="cell" spacing={2} alignItems="center">
    <Typography
      color="textPrimary"
      variant="subtitle2"
      sx={{ flex: '0 0 128px' }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      color="textSecondary"
      variant="body2"
      sx={{ flex: '1' }}
    >
      {children}
    </Typography>
  </Stack>
)

interface StatusIndicatorProps {
  color: BoxProps['color'];
  sx?: SxProps;
  showViewLog?: boolean;
  viewLogClick?: MouseEventHandler<HTMLAnchorElement>;
  title?: string;
}

export const StatusIndicator : FC<StatusIndicatorProps> = ({
  color,
  sx,
  showViewLog,
  viewLogClick,
  title,
}) => {
  const finalSx = merge({ display: 'flex', alignContent: 'center'}, sx)
  const roundSpot = 
    <Box
      role="figure"
      sx={{
        display: 'inline-block',
        borderRadius: '50%',
        width: 24,
        height: 24,
        backgroundColor: color,
        mr: 1,
      }}
    />
  return (
    <Box sx={finalSx} >
      {title ?
        <Tooltip
          title={title}
        >
          {roundSpot}
        </Tooltip> :
        roundSpot
      }
      {showViewLog &&
        <Link
          component="button"
          color="secondary"
          underline="hover"
          onClick={viewLogClick}
          sx={{ cursor: 'pointer' }}
        >
          <Typography
            component="span"
            variant="caption"
          >
            (View Log)

          </Typography>
        </Link> 
      }
    </Box>
  )
}
